// src/pages/create-instance/components/columns.ts
import { Tooltip } from 'antd'

import { ServerDomainInfo } from '@/api/type-server'
import InfoIcon from '@/assets/icons/info-i.svg?react'
import { Trans } from '@/components'
import {
  BILLING_METHODS,
  type BillingMethod,
  DURATION,
  type Duration,
  ItemPriceType,
} from '@/consts/order'
import { Machine } from '@/models/machine'
import { formatPrice, formatRamMB } from '@/utils/formatter'
import { cn } from '@/utils/style'

import { findSubscriptionPlan, getPlanPriceByType } from '../utils'

export const createColumns = (
  isGPU: boolean,
  domainInfos: ServerDomainInfo[] = [],
  billingMethod?: BillingMethod,
  duration?: Duration,
) => {
  const isSubscription = billingMethod === BILLING_METHODS.SUBSCRIPTION && duration !== undefined

  const sharedColumns = [
    {
      title: (
        <div className="t-s-sb">
          <Trans i18nKey="create.machineTableHeaderCpuRam">RAM</Trans>
        </div>
      ),
      dataIndex: ['unitSpec', 'ramMB'],
      key: 'ramMB',
      width: 120,
      render: (text: number) => <div className="t-s-sb text-primary">{formatRamMB(text)}</div>,
    },
    {
      title: (
        <div className="t-s-sb">
          <Trans i18nKey="create.machineTableHeaderDiskSize">Storage</Trans>
        </div>
      ),
      dataIndex: ['unitSpec', 'diskGB'],
      key: 'diskGB',
      width: 120,
      render: (text: number) => <div className="t-s-sb text-primary">{text} GB</div>,
    },
    {
      title: (
        <div className="t-s-sb">
          {isSubscription ? (
            duration === DURATION.MONTHLY ? (
              <Trans i18nKey="create.machineTableHeaderPriceMonthly">Price/month</Trans>
            ) : (
              <Trans i18nKey="create.machineTableHeaderPriceDaily">Price/day</Trans>
            )
          ) : (
            <Trans i18nKey="create.machineTableHeaderPrice">Price/h</Trans>
          )}
        </div>
      ),
      dataIndex: ['priceObject', 'price'],
      key: 'price',
      width: 170,
      fixed: 'right' as const,
      render: (value: number, record: Machine) => {
        if (isSubscription && duration) {
          const plan = findSubscriptionPlan(record.items, duration)
          const priceCent = getPlanPriceByType(plan, ItemPriceType.Original)

          return (
            <div className="t-sb text-primary">
              {priceCent === undefined
                ? '-'
                : formatPrice(priceCent, false, Math.abs(priceCent) > 100 ? 'Credits' : 'Credit')}
            </div>
          )
        }

        return (
          <div className="mt-0.5">
            {record.hasDiscount ? (
              <div className="flex-col items-center justify-center text-center">
                <div className="text-highlight t-sb">{formatPrice(value)}</div>
                <div className="text-tertiary t-x-m line-through">
                  {formatPrice(
                    record.originPriceObject.price,
                    false,
                    record.originPriceObject.price > 100 ? 'Credits' : 'Credit',
                  )}
                </div>
              </div>
            ) : (
              <div className="t-sb text-primary">
                {formatPrice(value, false, value > 100 ? 'Credits' : 'Credit')}
              </div>
            )}
          </div>
        )
      },
    },
  ]

  const gpuSpecificColumns = [
    {
      title: (
        <div className="t-s-sb">
          <Trans i18nKey="create.machineTableHeaderGpu">GPU Model</Trans>
        </div>
      ),
      dataIndex: ['unitSpec', 'gpuName'],
      key: 'gpuName',
      width: 240,
      fixed: 'left' as const,
      render: (text: string, record: Machine) => (
        <div className="flex items-center justify-between gap-2 t-sb text-primary">
          {text}
          {(record.unitSpec.attributes.isVGPU || record.unitSpec.attributes.isMIG) && (
            <span
              className={cn(
                'flex items-center justify-center px-1.5 py-0.5 rounded-[4px] border border-neutral-200 bg-neutral-100 text-secondary t-x-sb',
              )}
            >
              vGPU
            </span>
          )}
        </div>
      ),
    },
    {
      title: (
        <div className="t-s-sb">
          <Trans i18nKey="create.machineTableHeaderGpus">GPUs</Trans>
        </div>
      ),
      dataIndex: ['unitSpec', 'gpuCores'],
      key: 'gpuCores',
      width: 120,
      render: (text: number) => <div className="t-s-sb text-primary">{text}</div>,
    },
    {
      title: (
        <div className="t-s-sb">
          <Trans i18nKey="create.machineTableHeaderGpuRam">GPU RAM</Trans>
        </div>
      ),
      dataIndex: ['unitSpec', 'gpuRamMB'],
      key: 'gpuRamMB',
      width: 120,
      render: (text: number) => <div className="t-s-sb text-primary">{formatRamMB(text)}</div>,
    },
    {
      title: (
        <div className="t-s-sb">
          <Trans i18nKey="create.machineTableHeaderRegion">Region</Trans>
        </div>
      ),
      dataIndex: 'domains',
      key: 'domains',
      width: 120,
      render: (domains: number[]) => {
        if (!domains || domains.length === 0) return <div className="t-s-sb text-primary">-</div>

        const domainNames = (domains || [])
          .map(
            (domain) =>
              domainInfos.find((info) => info.domain === domain)?.displayName || `${domain + 1}`,
          )
          .join(', ')

        return <div className="t-s-sb text-primary">{domainNames}</div>
      },
    },
    {
      title: (
        <div className="t-s-sb">
          <Trans i18nKey="create.machineTableHeaderAvailable">Avail Unit</Trans>
        </div>
      ),
      dataIndex: 'maxAvailUnits',
      key: 'maxAvailUnits',
      width: 120,
      render: (text: number) => (
        <div className={cn('t-s-sb text-btn-secondary', { 'text-error': text === 0 })}>{text}</div>
      ),
    },
    {
      title: (
        <div className="t-s-sb">
          <Trans i18nKey="create.machineTableHeaderVCpus">vCPUs</Trans>
        </div>
      ),
      dataIndex: ['unitSpec', 'cpuCores'],
      key: 'cpuCores',
      width: 120,
      render: (text: number, record: { unitSpec: { cpuName: string } }) => (
        <div
          className={cn('t-s-sb flex justify-start items-center space-x-2', {
            'text-error': text === 0,
          })}
        >
          <div>{text}</div>
          <Tooltip title={<div className="text-center t-sm">{record.unitSpec.cpuName}</div>}>
            <>
              <InfoIcon height={16} width={16} className="text-tertiary" />
            </>
          </Tooltip>
        </div>
      ),
    },
  ]

  const cpuSpecificColumns = [
    {
      title: (
        <div className="t-s-sb">
          <Trans i18nKey="create.machineTableHeaderCpu">CPU Model</Trans>
        </div>
      ),
      dataIndex: ['unitSpec', 'cpuName'],
      key: 'cpuName',
      width: 240,
      fixed: 'left' as const,
      render: (text: string) => <div className="t-s-sb text-primary">{text}</div>,
    },
    {
      title: (
        <div className="t-s-sb">
          <Trans i18nKey="create.machineTableHeaderRegion">Region</Trans>
        </div>
      ),
      dataIndex: 'domains',
      key: 'domains',
      width: 120,
      render: (domains: number[]) => {
        if (!domains || domains.length === 0) return <div className="t-s-sb text-primary">-</div>

        const domainNames = (domains || [])
          .map(
            (domain) =>
              domainInfos.find((info) => info.domain === domain)?.displayName || `${domain + 1}`,
          )
          .join(', ')

        return <div className="t-s-sb text-primary">{domainNames}</div>
      },
    },
    {
      title: (
        <div className="t-s-sb">
          <Trans i18nKey="create.machineTableHeaderAvailable">Avail Unit</Trans>
        </div>
      ),
      dataIndex: 'maxAvailUnits',
      key: 'maxAvailUnits',
      width: 120,
      render: (text: number) => (
        <div className={cn('t-s-sb text-btn-secondary', { 'text-error': text === 0 })}>{text}</div>
      ),
    },
    {
      title: (
        <div className="t-s-sb">
          <Trans i18nKey="create.machineTableHeaderVCpus">vCPUs</Trans>
        </div>
      ),
      dataIndex: ['unitSpec', 'cpuCores'],
      key: 'cpuCores',
      width: 120,
      render: (text: number) => <div className="t-s-sb text-primary">{text}</div>,
    },
  ]

  return isGPU
    ? [...gpuSpecificColumns, ...sharedColumns]
    : [...cpuSpecificColumns, ...sharedColumns]
}
